import React, { useEffect, useMemo } from "react";
import { withTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
} from "@mui/material";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import { omit } from "lodash";

import SelectField from "Components/Common/SelectField";
import {
  getOptionValue,
  powerRequirementOptions,
  utilityOptionsForUser,
} from "common/enums";
import Modal from "Components/Common/Modal";
import InputFieldText from "Components/Common/InputFieldText";
import { Text } from "Components/MeterInfoForm/ConsumerDetails/styles";
import Backdrop from "Components/Common/Backdrop";

import { filterEmptyStrings } from "utils";

import {
  useGetLazyGetUserApproverDetails,
  useGetSignatureApproverUsers,
  usePostUserApproverMutation,
} from "apis/User";
import { useLazyPremiseTypeQuery } from "apis/PremiseType";

const UserManagementForm = ({ id, t, handleClose }) => {
  const [
    getApproverDetails,
    { data: { data: userData } = {}, isLoading: isApproverDataLoading },
  ] = useGetLazyGetUserApproverDetails();

  const { data: { data: approverUsers } = {} } = useGetSignatureApproverUsers();

  const [getPremiseTypes, { data: { data: premiseTypes } = {} }] =
    useLazyPremiseTypeQuery();

  const [addUserApprover, { isLoading }] = usePostUserApproverMutation();

  const { user: { name } = {}, role: { name: roleName } = {} } = userData || {};

  const updateApprovers = useMemo(
    () =>
      approverUsers?.length
        ? approverUsers.map(({ id, name, roleId }) => ({
            label: name,
            value: id,
            roleId,
          }))
        : [],
    [approverUsers]
  );

  const initialValues = useMemo(
    () => ({
      id: id || 0,
      userId: userData?.user?.id ?? "",
      loadType: userData?.loadType ?? "",
      minimumAmount: userData?.minimumAmount ?? "",
      maximumAmount: userData?.maximumAmount ?? "",
      roleId: userData?.role?.id ?? 0,
      isTechnicalSanction: userData?.isTechnicalSanction ?? false,
      isTemporaryConnection: userData?.isTechnicalSanction ?? false,
      utilityId: userData?.utilityId?.length
        ? userData.utilityId.includes(getOptionValue("utilityOptions", "Water"))
          ? getOptionValue(
              "utilityOptionsForUser",
              "Water, Sewage, Solid Waste"
            )
          : userData.utilityId[0]
        : "",
      premiseTypeId: userData?.premiseType?.id ?? "",
    }),
    [userData]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: Yup.object({
      userId: Yup.string().required("User is required"),
      utilityId: Yup.string().required("Utility is required"),
      loadType: Yup.string().test("isLoadTypeRequired", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (
          current.utilityId === getOptionValue("utilityOptions", "Power") &&
          !current.loadType
        ) {
          return this.createError({
            message: "Load type is required",
          });
        }

        return true;
      }),
      premiseTypeId: Yup.string().test(
        "isPremiseTypeIdRequired",
        "",
        function () {
          const [{ value: current = null } = {}] = this.from || [];

          if (
            current.utilityId === getOptionValue("utilityOptions", "DCS") &&
            !current.premiseTypeId
          ) {
            return this.createError({
              message: "Premise type is required",
            });
          }

          return true;
        }
      ),
      minimumAmount: Yup.number().required("Minimum amount is required"),
      maximumAmount: Yup.number().moreThan(
        Yup.ref("minimumAmount"),
        "Maximum amount must be more then minimum amount"
      ),
    }),
    onSubmit: async (values) => {
      try {
        const isOtherUtilities =
          getOptionValue(
            "utilityOptionsForUser",
            "Water, Sewage, Solid Waste"
          ) === values.utilityId;
        const payload = filterEmptyStrings(values);
        payload.utilityId = isOtherUtilities
          ? [
              getOptionValue("utilityOptions", "Water"),
              getOptionValue("utilityOptions", "Sewage"),
              getOptionValue("utilityOptions", "Solid Waste"),
            ]
          : [values.utilityId];
        await addUserApprover({
          payload: omit(
            payload,
            !isOtherUtilities ? "isTemporaryConnection" : ""
          ),
          toastMessage: `Approver ${id ? "Updated" : "added"} successfully`,
        });
        handleClose();
      } catch (error) {}
    },
  });

  const {
    values: {
      isTechnicalSanction,
      userId,
      roleId,
      utilityId,
      isTemporaryConnection,
    },
    setFieldValue,
    handleSubmit,
  } = formik;

  const isPowerUtilitySelected = useMemo(
    () => utilityId === getOptionValue("utilityOptions", "Power"),
    [utilityId]
  );

  useEffect(() => {
    try {
      if (id) {
        (async () => {
          await getApproverDetails(id);
        })();
      }
    } catch (error) {}
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        await getPremiseTypes({
          utilityId:
            utilityId === getOptionValue("utilityOptions", "Power")
              ? getOptionValue("utilityOptions", "Power")
              : "",
        });
      } catch (error) {}
    })();
  }, [utilityId]);

  if (isApproverDataLoading) {
    return <Backdrop open />;
  }

  return (
    <Modal
      isOpen
      handleClose={handleClose}
      title={t(`${id ? "Update" : "Add"} Approver Details`)}
      maxWidth="md"
    >
      <Grid container columnSpacing={2} className="mt-3">
        {!!id && (
          <Grid container item columnSpacing={2} xs={12}>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("User Name")}: </b>
                {name}
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("Role")}: </b>
                {roleName}
              </Text>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Text>
                <b>{t("Approver Of")}: </b>
                {isTechnicalSanction ? "Technical Sanction" : "Demand Estimate"}
              </Text>
            </Grid>
          </Grid>
        )}
        {!id && (
          <Grid item xs={12} sm={6} md={4}>
            <SelectField
              label={t("Select User") + "*"}
              name="userId"
              value={updateApprovers.find(
                ({ value, roleId: userRoleId }) =>
                  value === userId && userRoleId === roleId
              )}
              onChange={(_, option) => {
                setFieldValue("userId", option.value);
                setFieldValue("roleId", option.roleId);
              }}
              formik={formik}
              options={updateApprovers}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <SelectField
            label={t("Utility") + "*"}
            name="utilityId"
            formik={formik}
            onChange={(_, { value }) => {
              setFieldValue("utilityId", value);
              if (value !== getOptionValue("utilityOptions", "DCS")) {
                setFieldValue("premiseTypeId", "");
              }
              if (value !== getOptionValue("utilityOptions", "Power")) {
                setFieldValue("loadType", "");
              }
              if (
                value !==
                getOptionValue("utilityOptions", "Water, Sewage, Solid Waste")
              ) {
                setFieldValue("isTemporaryConnection", false);
              }
            }}
            options={utilityOptionsForUser}
          />
        </Grid>
        {getOptionValue("utilityOptionsForUser", "DCS") === utilityId && (
          <Grid item xs={12} sm={6} md={4}>
            <SelectField
              label={t("Premise Type") + "*"}
              name="premiseTypeId"
              formik={formik}
              disabled={!utilityId}
              options={premiseTypes || []}
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id}
            />
          </Grid>
        )}
        {isPowerUtilitySelected && (
          <Grid item xs={12} sm={6} md={4}>
            <SelectField
              label={t("Load Type") + "*"}
              name="loadType"
              formik={formik}
              options={powerRequirementOptions}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            label={t("Minimum Amount") + "*"}
            name="minimumAmount"
            type="number"
            formik={formik}
            placeholder="Enter Minimum Amount"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            label={t("Maximum Amount")}
            name="maximumAmount"
            type="number"
            formik={formik}
            placeholder="Enter Maximum Amount"
          />
        </Grid>
        {!id && (
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!isTechnicalSanction}
                  onChange={() =>
                    setFieldValue("isTechnicalSanction", !isTechnicalSanction)
                  }
                />
              }
              label={t("Technical Sanction")}
            />
          </Grid>
        )}
        {getOptionValue(
          "utilityOptionsForUser",
          "Water, Sewage, Solid Waste"
        ) === utilityId && (
          <Grid item xs={12} sm={6} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!isTemporaryConnection}
                  onChange={() =>
                    setFieldValue(
                      "isTemporaryConnection",
                      !isTemporaryConnection
                    )
                  }
                />
              }
              label={t("Temporary Connection")}
            />
          </Grid>
        )}
        <Grid item xs={12} className="text-end mt-3">
          <Button
            onClick={handleSubmit}
            color="primary"
            className="mb-1 me-1"
            variant="contained"
            disabled={isLoading}
          >
            {t("Save")} {isLoading && <CircularProgress size={20} />}
          </Button>
        </Grid>
      </Grid>
    </Modal>
  );
};

UserManagementForm.propTypes = {
  id: PropTypes.number,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(UserManagementForm);
